import { Button } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo } from 'react';
import event from '~helpers/mixpanel';
import hexToRGBA from '~helpers/hex-to-rgba';
import { processEventNameMap } from '../header';
import OnboardingContext, { onboardingProcesses } from '../context';
import { pageType } from '~constants/pages';

const useStyles = makeStyles(
  theme => ({
    wrap: {
      ...theme.flex.mainEnd,
      position: 'fixed',
      bottom: 0,
      width: '100%',
      height: 80,
      padding: '20px 56px',
      zIndex: 1000,
      boxShadow: theme.shadow.componentShallow,
      backgroundColor: theme.colors.gray1,
    },
    btnWrap: {
      ...theme.flex.mainEnd,
    },
    defaultFont: {
      ...theme.typography.h4,
      color: `${hexToRGBA(theme.colors.gray10, 0.65)}`,
    },
    popconfirmWrap: {
      width: 396,
      color: `${hexToRGBA(theme.colors.gray10, 0.65)}`,
    },
    btnFont: {
      ...theme.typography.h4,
    },
  }),
  { name: 'Footer' },
);

const Footer = ({ onBack = () => {}, onNext = () => {} }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const onboardingData = useContext(OnboardingContext);
  const { process, propertyTypeSlug, residentTypeSlug } = onboardingData;

  const handleClickBack = useCallback(() => {
    const eventObj = {
      'Clicked Position': 'Back',
      'Current Page Type': pageType.onBoardingPage,
    };
    if (process !== onboardingProcesses.RECALLED_TO && process !== onboardingProcesses.SUCCESSFUL) {
      Object.assign(eventObj, { 'Property Type': propertyTypeSlug });
    }
    if (process === onboardingProcesses.RESIDENT_TYPE) {
      Object.assign(eventObj, {
        'Resident Type': t(`landlord.onboarding.resident_type.${residentTypeSlug.toLowerCase()}`),
      });
    }
    onBack();
    event(processEventNameMap[process], eventObj);
  }, [onBack, process, propertyTypeSlug, residentTypeSlug, t]);

  const handleClickNext = useCallback(() => {
    const eventObj = {
      'Clicked Position': 'Next Btn',
      'Current Page Type': pageType.onBoardingPage,
    };
    if (process !== onboardingProcesses.RECALLED_TO && process !== onboardingProcesses.SUCCESSFUL) {
      Object.assign(eventObj, { 'Property Type': propertyTypeSlug });
    }
    if (process === onboardingProcesses.RESIDENT_TYPE) {
      Object.assign(eventObj, {
        'Resident Type': t(`landlord.onboarding.resident_type.${residentTypeSlug.toLowerCase()}`),
      });
    }
    onNext();
    event(processEventNameMap[process], eventObj);
  }, [onNext, process, propertyTypeSlug, residentTypeSlug, t]);

  const hasBackBtn = useMemo(
    () =>
      [
        onboardingProcesses.RESIDENT_TYPE,
        onboardingProcesses.PROPERTY_ADDRESS,
        onboardingProcesses.PROPERTY_NAME,
      ].includes(process),
    [process],
  );

  const isNextBtnDisabled = useMemo(() => {
    const { propertyTypeSlug, residentTypeSlug, mapAddressData, propertyName, coverPhoto } =
      onboardingData;
    const { city, cityId, country, street, zipcode, coordinates } = mapAddressData;

    switch (process) {
      case onboardingProcesses.PROPERTY_TYPE:
        return !propertyTypeSlug;
      case onboardingProcesses.RESIDENT_TYPE:
        return !residentTypeSlug;
      case onboardingProcesses.PROPERTY_ADDRESS:
        return (
          Object.values({
            city,
            cityId,
            country,
            street,
            zipcode,
          }).some(item => !item) ||
          !coordinates.lat ||
          !coordinates.lng
        );
      case onboardingProcesses.PROPERTY_NAME:
        return !propertyName;
      case onboardingProcesses.COVER_PHOTO:
        return !coverPhoto;
      default:
        return false;
    }
  }, [onboardingData, process]);

  return (
    <div className={classes.wrap}>
      <div className={classes.btnWrap}>
        <If condition={hasBackBtn}>
          <Button
            type="link"
            size="large"
            onClick={handleClickBack}
            className={classes.defaultFont}
          >
            {t('landlord.onboarding.btn.back')}
          </Button>
        </If>
        {process !== onboardingProcesses.RECALLED_TO && (
          <Button
            type="primary"
            className={classes.btnFont}
            size="large"
            disabled={isNextBtnDisabled}
            loading={onboardingData.createPropertyLoading || onboardingData?.updateGalleryLoading}
            onClick={handleClickNext}
          >
            {t('landlord.onboarding.btn.next')}
          </Button>
        )}
      </div>
    </div>
  );
};

Footer.propTypes = {
  onBack: PropTypes.func,
  onNext: PropTypes.func,
};

export default Footer;
